import {
  createSlice,
  createAsyncThunk,
  isPending,
  isRejected,
  PayloadAction
} from '@reduxjs/toolkit'
import { SessionResponse } from '../../services/endpoints/session'
import {
  getLegacyUser,
  getUser,
  refreshToken
} from '../../services/endpoints'
import {
  SessionData,
  getFromLocalStorage,
  flushToLocalStorage,
  LocalStorageUser,
  _saveOrDeleteLocalStorageItem,
  _getJsonFromUser,
  Preferences
} from '../lib/localStorage'
import { NetworkRequestError } from '../../services/lib'
import {
  postLogin,
  handleReturnAuth,
  deleteSession
} from '../../services/endpoints'
import { DefaultState } from '../../types'
import { formatError } from '../lib/errors'
import { UserpreferenceSlice } from './userPreferenceSlice'
import { withTimeout, Mutex } from 'async-mutex'

const sliceName = 'session'

export interface UserData {
  username: string
  password: string
}

export interface SessionState {
  data: SessionData
  loading: boolean
  error: NetworkRequestError | null
  passwordExpired: boolean
}

const mutex = withTimeout(new Mutex(), 10000, new Error('Could not acquire the session mutex'))

export const initialSessionState: SessionState = {
  data: getFromLocalStorage(),
  loading: false,
  error: null,
  passwordExpired: false
}

export const _createSessionData = (
  session: SessionResponse,
  user: LocalStorageUser
): SessionData => {
  const { profile } = user
  return {
    accessToken: session.access_token,
    refreshToken: session.refresh_token,
    expiresAt: session.expires_at,
    expiresIn: session.expires_in,
    session: { username: profile.username, time: session.expires_at },
    user
  }
}

export const _updateSessionDataUser = (
  data: SessionData,
  user: LocalStorageUser
): SessionData => {
  const { profile } = user
  if (data.accessToken?.includes('"')) {
    data.accessToken = data.accessToken.replaceAll('"', '')
  }
  if (data.refreshToken?.includes('"')) {
    data.refreshToken = data.refreshToken.replaceAll('"', '')
  }
  return {
    ...data,
    session: { username: profile.username, time: data.expiresAt! },
    user: user
  }
}

export const _getBlankSessionData = (): SessionData => {
  return {
    accessToken: null,
    refreshToken: null,
    user: null,
    expiresAt: null,
    expiresIn: null,
    session: null
  }
}

export const loginUser = createAsyncThunk(
  `${sliceName}/login`,
  async (login: UserData, { dispatch, rejectWithValue }) => {
    try {
      const session = await postLogin(login)
      const user = await formatToLS(session.access_token)
      const sessionData = _createSessionData(session, user)

      dispatch(UserpreferenceSlice.actions.updatePreferences(user))
      flushToLocalStorage({ ...sessionData, user })

      return { ...sessionData, user }
    }
     catch (err: unknown) {
      const e = formatError(err)

      if (e != undefined && e.message == 'Password is expired') {
        return {
          passwordExpired: true
        }
      }

      return rejectWithValue(e)
    }
  }
)

export const handlePostAuthSession = createAsyncThunk(
  `${sliceName}/oauthSession`,
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const session = await handleReturnAuth()
      const user = await formatToLS(session.access_token)
      const sessionData = _createSessionData(session, user)
      dispatch(UserpreferenceSlice.actions.updatePreferences(user))

      return sessionData
    } catch (_) {
      return rejectWithValue({
        code: 'auth',
        message: 'Authentication failed',
        fields: []
      })
    }
  }
)

export const refreshSession = createAsyncThunk(
  `${sliceName}/refreshToken`,
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      return mutex.runExclusive(async () => {
        const state = getState() as DefaultState

        if (!state.session.data.refreshToken) {
          return rejectWithValue({
            code: 'auth',
            message: 'Authentication failed',
            fields: []
          })
        }

        const session = await refreshToken(
          state.session.data.refreshToken.replaceAll('"', '')
        )

        const user = await formatToLS(session.access_token)
        const sessionData = _createSessionData(session, user)

        //todo - should be able to do this in extraReducers
        dispatch(UserpreferenceSlice.actions.updatePreferences(user))

        return sessionData
      })
    } catch (err: any) {
      return rejectWithValue(err)
    }
  }
)

export const refreshUser = createAsyncThunk(
  `${sliceName}/refreshUser`,
  async (_, { rejectWithValue }) => {
    try {
      return mutex.runExclusive(async () => {
        const token = getFromLocalStorage().accessToken
        const user = await formatToLS(token ? token.replaceAll('"', '') : '')
        return user
      })
    } catch (err: unknown) {
      return rejectWithValue(err)
    }
  }
)

export const logoutUser = createAsyncThunk(
  `${sliceName}/logout`,
  async (_, { rejectWithValue, dispatch }) => {
    try {
      return mutex.runExclusive(async () => {
        await deleteSession()
        flushToLocalStorage(_getBlankSessionData())
        dispatch(UserpreferenceSlice.actions.updatePreferences(null))
        return true
      })
    } catch (err: unknown) {
      return rejectWithValue(err)
    }
  }
)

const formatToLS = async (token: string): Promise<LocalStorageUser> => {
  try{
    const user = await getUser(token)
    if(!user){
      throw new Error('User not found')
    }
    const { administrator, permissions, preferences } = await getLegacyUser(token) 
    return { ...user, administrator, permissions, preferences }
  }
  catch(e){
    throw { // Throw again to be caught by the calling function
      code: 'auth',
      message: 'Authentication failed', 
      fields: []
    }
  }
}

export const sessionSlice = createSlice({
  name: sliceName,
  initialState: initialSessionState,
  reducers: {
    loadSession: (state) => {
      state.data = getFromLocalStorage()
      return state
    },
    delSession: (state) => {
      state.data = _getBlankSessionData()
      state.error = null
      state.loading = false
      state.passwordExpired = false
      flushToLocalStorage(state.data)

      return state
    },
    updatePreferences: (state, action: PayloadAction<{name:string, id:number, val:string}>) => {
      if(state.data.user){
        state.data.user.preferences[action.payload.name] = {id: action.payload.id, val: action.payload.val}
        flushToLocalStorage(state.data)
      }
      else{
        throw new Error("Tried saving to local storage with no user")
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false
        state.error = null

        if ('passwordExpired' in action.payload) {
          state.passwordExpired = true
          state.data = _getBlankSessionData()
        } else {
          state.data = action.payload
          state.passwordExpired = false
        }

        flushToLocalStorage(state.data)
      })
      .addCase(handlePostAuthSession.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        state.data = action.payload

        flushToLocalStorage(state.data)
      })
      .addCase(refreshSession.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        state.data = action.payload

        flushToLocalStorage(state.data)
      })
      .addCase(refreshSession.rejected, (state, action) => {
        state.data = _getBlankSessionData()
        state.loading = false
        state.passwordExpired = false

        flushToLocalStorage(state.data)
      })
      .addCase(refreshUser.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        state.data = _updateSessionDataUser(state.data, action.payload)
        flushToLocalStorage(state.data)
      })
      .addCase(refreshUser.rejected, (state, action) => {
        state.data = _getBlankSessionData()
        state.loading = false
        state.passwordExpired = false

        flushToLocalStorage(state.data)
      })
      .addCase(logoutUser.fulfilled, (state, _action) => {
        state.loading = false
        state.error = null
        state.data = _getBlankSessionData()

        flushToLocalStorage(state.data)
      })
      .addMatcher(isPending, (state: SessionState, action) => {
        if (sliceName === action.type.split('/')[0]) {
          state.loading = true
        }
      })
      .addMatcher(
        isRejected,
        (state, action: PayloadAction<NetworkRequestError>) => {
          if (sliceName === action.type.split('/')[0]) {
            state.loading = false
            state.error = action.payload
          }
        }
      )
  }
})